import type { ApiContext } from "../context";
import {
  type ApiNotAuthorized,
  ErrorCode,
  EventNotFound,
  HostingOff,
  InvalidFormField,
  LocationNotFound,
  ShowCancelled,
  StoreNotFound,
  type StoreRoleForbidden,
  TableKindNotFound,
  TableNotFound,
  TableRefundRefused,
  TablesTaken,
  TicketKindNotFound,
  type TransportError,
  UnexpectedApiError,
  ValidationError,
} from "../errors";
import { parse } from "../model/common";
import { EventTableKind, EventTableKinds, type NewTableKind, type TableKindUpdate } from "../model/event-table";
import { EventTicketKind, EventTicketKinds, type NewTicketKind, type TicketKindUpdate } from "../model/event-ticket";
import {
  HostedEvent,
  HostedEventRefunds,
  HostedEvents,
  type HostedEventUpdate,
  type NewHostedEvent,
} from "../model/hosted-event";
import { fail, forward, type Result } from "../result";
import type { Transport } from "../transport";
import { asRoleForbidden } from "./role";

/** Recognises the refusal of a shop staff have not switched on for hosting. */
function asHostingOff(e: ValidationError): HostingOff | undefined {
  return e.code === ErrorCode.CapabilityRequired && e.incorrectValue === "HOST_EVENTS"
    ? new HostingOff(e.detailedDesc, e)
    : undefined;
}

/** Recognises the refusal to change a show its host cancelled. */
function asShowCancelled(e: ValidationError): ShowCancelled | undefined {
  return e.code === ErrorCode.IncorrectState && e.incorrectValue === "cancelledAt"
    ? new ShowCancelled(e.detailedDesc, e)
    : undefined;
}

/**
 * `/stores/:storeId/events`: the card shows a shop hosts. Reading is open to every
 * member; creating, editing and publishing need a Manager and hosting switched on
 * (`canHost`); taking a show back to a draft, cancelling it and taking a table back
 * need only the Manager. Nothing about a cancelled show changes again.
 */
export class HostedEventsApi {
  constructor(private readonly t: Transport) {}

  async list(ctx: ApiContext, storeId: string): Promise<Result<HostedEvents, ApiNotAuthorized | StoreNotFound>> {
    const r = await this.t.get(ctx, `/stores/${storeId}/events`);
    return forward(
      r,
      (j) => parse(HostedEvents, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  async get(
    ctx: ApiContext,
    storeId: string,
    eventId: string,
  ): Promise<Result<HostedEvent, ApiNotAuthorized | StoreNotFound | EventNotFound>> {
    // Sent as it is, an empty id would reach the list.
    if (!eventId) return fail(new EventNotFound(eventId));
    const r = await this.t.get(ctx, `/stores/${storeId}/events/${eventId}`);
    return forward(
      r,
      (j) => parse(HostedEvent, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          if (e.incorrectValue === "eventId") return new EventNotFound(eventId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /** Creates the show as a draft. A refused field comes back as `InvalidFormField`, the address ones by their own names. */
  async create(
    ctx: ApiContext,
    storeId: string,
    event: NewHostedEvent,
  ): Promise<
    Result<
      HostedEvent,
      ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | HostingOff | LocationNotFound | InvalidFormField
    >
  > {
    const body = { ...event, startsAt: event.startsAt.toISOString(), endsAt: event.endsAt.toISOString() };
    const r = await this.t.post(ctx, `/stores/${storeId}/events`, { body });
    return forward(
      r,
      (j) => parse(HostedEvent, j),
      (e) => {
        if (e instanceof ValidationError) {
          const refused = asRoleForbidden(e) ?? asHostingOff(e);
          if (refused) return refused;
          if (e.incorrectValue === "storeId" && e.code === ErrorCode.DoesntExist) return new StoreNotFound(storeId, e);
          if (e.incorrectValue === "locationId" && e.code === ErrorCode.DoesntExist) {
            return new LocationNotFound(event.locationId ?? "", e);
          }
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  async update(
    ctx: ApiContext,
    storeId: string,
    eventId: string,
    update: HostedEventUpdate,
  ): Promise<
    Result<
      HostedEvent,
      | ApiNotAuthorized
      | StoreNotFound
      | StoreRoleForbidden
      | HostingOff
      | EventNotFound
      | ShowCancelled
      | InvalidFormField
    >
  > {
    if (!eventId) return fail(new EventNotFound(eventId));
    const body = { ...update, startsAt: update.startsAt?.toISOString(), endsAt: update.endsAt?.toISOString() };
    const r = await this.t.patch(ctx, `/stores/${storeId}/events/${eventId}`, { body });
    return forward(
      r,
      (j) => parse(HostedEvent, j),
      (e) => {
        if (e instanceof ValidationError) {
          const refused = asRoleForbidden(e) ?? asHostingOff(e) ?? asShowCancelled(e);
          if (refused) return refused;
          if (e.incorrectValue === "storeId" && e.code === ErrorCode.DoesntExist) return new StoreNotFound(storeId, e);
          if (e.incorrectValue === "eventId") return new EventNotFound(eventId, e);
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  /**
   * Uploads the show's flyer and sets it, replacing any earlier one: `image` is a data URL of a PNG, JPEG,
   * WEBP or GIF, up to 2 MB decoded. Remove it with `update({ flyerUrl: null })`.
   */
  async uploadFlyer(
    ctx: ApiContext,
    storeId: string,
    eventId: string,
    image: string,
  ): Promise<
    Result<
      HostedEvent,
      | ApiNotAuthorized
      | StoreNotFound
      | StoreRoleForbidden
      | HostingOff
      | EventNotFound
      | ShowCancelled
      | InvalidFormField
    >
  > {
    if (!eventId) return fail(new EventNotFound(eventId));
    const r = await this.t.post(ctx, `/stores/${storeId}/events/${eventId}/flyer`, { body: { image } });
    return forward(
      r,
      (j) => parse(HostedEvent, j),
      (e) => {
        if (e instanceof ValidationError) {
          const refused = asRoleForbidden(e) ?? asHostingOff(e) ?? asShowCancelled(e);
          if (refused) return refused;
          if (e.incorrectValue === "storeId" && e.code === ErrorCode.DoesntExist) return new StoreNotFound(storeId, e);
          if (e.incorrectValue === "eventId") return new EventNotFound(eventId, e);
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  /** Other shops see a published show; staff may still block it. */
  async publish(
    ctx: ApiContext,
    storeId: string,
    eventId: string,
  ): Promise<
    Result<
      HostedEvent,
      ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | HostingOff | EventNotFound | ShowCancelled
    >
  > {
    if (!eventId) return fail(new EventNotFound(eventId));
    const r = await this.t.post(ctx, `/stores/${storeId}/events/${eventId}/publish`);
    return forward(
      r,
      (j) => parse(HostedEvent, j),
      (e) => {
        if (e instanceof ValidationError) {
          const refused = asRoleForbidden(e) ?? asHostingOff(e) ?? asShowCancelled(e);
          if (refused) return refused;
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          if (e.incorrectValue === "eventId") return new EventNotFound(eventId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /** Takes a show back to a draft. Allowed after hosting is switched off. */
  async unpublish(
    ctx: ApiContext,
    storeId: string,
    eventId: string,
  ): Promise<Result<HostedEvent, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | EventNotFound>> {
    if (!eventId) return fail(new EventNotFound(eventId));
    const r = await this.t.post(ctx, `/stores/${storeId}/events/${eventId}/unpublish`);
    return forward(
      r,
      (j) => parse(HostedEvent, j),
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          if (e.incorrectValue === "eventId") return new EventNotFound(eventId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /** The show's kinds of table, each with every table and the shop holding it. Readable by every member. */
  async tableKinds(
    ctx: ApiContext,
    storeId: string,
    eventId: string,
  ): Promise<Result<EventTableKinds, ApiNotAuthorized | StoreNotFound | EventNotFound>> {
    if (!eventId) return fail(new EventNotFound(eventId));
    const r = await this.t.get(ctx, `/stores/${storeId}/events/${eventId}/table-kinds`);
    return forward(
      r,
      (j) => parse(EventTableKinds, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          if (e.incorrectValue === "eventId") return new EventNotFound(eventId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /** Adds a kind and its tables, numbered on from the show's highest number. */
  async createTableKind(
    ctx: ApiContext,
    storeId: string,
    eventId: string,
    kind: NewTableKind,
  ): Promise<
    Result<
      EventTableKind,
      | ApiNotAuthorized
      | StoreNotFound
      | StoreRoleForbidden
      | HostingOff
      | EventNotFound
      | ShowCancelled
      | InvalidFormField
    >
  > {
    if (!eventId) return fail(new EventNotFound(eventId));
    const r = await this.t.post(ctx, `/stores/${storeId}/events/${eventId}/table-kinds`, { body: kind });
    return forward(
      r,
      (j) => parse(EventTableKind, j),
      (e) => {
        if (e instanceof ValidationError) {
          const refused = asRoleForbidden(e) ?? asHostingOff(e) ?? asShowCancelled(e);
          if (refused) return refused;
          if (e.incorrectValue === "storeId" && e.code === ErrorCode.DoesntExist) return new StoreNotFound(storeId, e);
          if (e.incorrectValue === "eventId") return new EventNotFound(eventId, e);
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  /** `TablesTaken` when fewer tables would leave shops' tables out. */
  async updateTableKind(
    ctx: ApiContext,
    storeId: string,
    eventId: string,
    kindId: string,
    update: TableKindUpdate,
  ): Promise<
    Result<
      EventTableKind,
      | ApiNotAuthorized
      | StoreNotFound
      | StoreRoleForbidden
      | HostingOff
      | EventNotFound
      | ShowCancelled
      | TableKindNotFound
      | TablesTaken
      | InvalidFormField
    >
  > {
    if (!eventId) return fail(new EventNotFound(eventId));
    if (!kindId) return fail(new TableKindNotFound(kindId));
    const r = await this.t.patch(ctx, `/stores/${storeId}/events/${eventId}/table-kinds/${kindId}`, { body: update });
    return forward(
      r,
      (j) => parse(EventTableKind, j),
      (e) => {
        if (e instanceof ValidationError) {
          const refused = asRoleForbidden(e) ?? asHostingOff(e) ?? asShowCancelled(e);
          if (refused) return refused;
          if (e.incorrectValue === "storeId" && e.code === ErrorCode.DoesntExist) return new StoreNotFound(storeId, e);
          if (e.incorrectValue === "eventId") return new EventNotFound(eventId, e);
          if (e.incorrectValue === "kindId") {
            return e.code === ErrorCode.DoesntExist
              ? new TableKindNotFound(kindId, e)
              : new TablesTaken(e.detailedDesc, e);
          }
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  /** Only while no shop has taken a table of it. */
  async deleteTableKind(
    ctx: ApiContext,
    storeId: string,
    eventId: string,
    kindId: string,
  ): Promise<
    Result<
      void,
      | ApiNotAuthorized
      | StoreNotFound
      | StoreRoleForbidden
      | HostingOff
      | EventNotFound
      | ShowCancelled
      | TableKindNotFound
      | TablesTaken
    >
  > {
    if (!eventId) return fail(new EventNotFound(eventId));
    if (!kindId) return fail(new TableKindNotFound(kindId));
    const r = await this.t.delete(ctx, `/stores/${storeId}/events/${eventId}/table-kinds/${kindId}`);
    return forward(
      r,
      () => undefined,
      (e) => {
        if (e instanceof ValidationError) {
          const refused = asRoleForbidden(e) ?? asHostingOff(e) ?? asShowCancelled(e);
          if (refused) return refused;
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          if (e.incorrectValue === "eventId") return new EventNotFound(eventId, e);
          if (e.incorrectValue === "kindId") {
            return e.code === ErrorCode.DoesntExist
              ? new TableKindNotFound(kindId, e)
              : new TablesTaken(e.detailedDesc, e);
          }
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /** The show's kinds of ticket, in the host's order. Readable by every member. Nothing sells them yet. */
  async ticketKinds(
    ctx: ApiContext,
    storeId: string,
    eventId: string,
  ): Promise<Result<EventTicketKinds, ApiNotAuthorized | StoreNotFound | EventNotFound>> {
    if (!eventId) return fail(new EventNotFound(eventId));
    const r = await this.t.get(ctx, `/stores/${storeId}/events/${eventId}/ticket-kinds`);
    return forward(
      r,
      (j) => parse(EventTicketKinds, j),
      (e) => {
        if (e instanceof ValidationError) {
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          if (e.incorrectValue === "eventId") return new EventNotFound(eventId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /** Adds a kind of ticket, last in the show's order; no capacity means no limit. */
  async createTicketKind(
    ctx: ApiContext,
    storeId: string,
    eventId: string,
    kind: NewTicketKind,
  ): Promise<
    Result<
      EventTicketKind,
      | ApiNotAuthorized
      | StoreNotFound
      | StoreRoleForbidden
      | HostingOff
      | EventNotFound
      | ShowCancelled
      | InvalidFormField
    >
  > {
    if (!eventId) return fail(new EventNotFound(eventId));
    const r = await this.t.post(ctx, `/stores/${storeId}/events/${eventId}/ticket-kinds`, { body: kind });
    return forward(
      r,
      (j) => parse(EventTicketKind, j),
      (e) => {
        if (e instanceof ValidationError) {
          const refused = asRoleForbidden(e) ?? asHostingOff(e) ?? asShowCancelled(e);
          if (refused) return refused;
          if (e.incorrectValue === "storeId" && e.code === ErrorCode.DoesntExist) return new StoreNotFound(storeId, e);
          if (e.incorrectValue === "eventId") return new EventNotFound(eventId, e);
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  async updateTicketKind(
    ctx: ApiContext,
    storeId: string,
    eventId: string,
    kindId: string,
    update: TicketKindUpdate,
  ): Promise<
    Result<
      EventTicketKind,
      | ApiNotAuthorized
      | StoreNotFound
      | StoreRoleForbidden
      | HostingOff
      | EventNotFound
      | ShowCancelled
      | TicketKindNotFound
      | InvalidFormField
    >
  > {
    if (!eventId) return fail(new EventNotFound(eventId));
    if (!kindId) return fail(new TicketKindNotFound(kindId));
    const r = await this.t.patch(ctx, `/stores/${storeId}/events/${eventId}/ticket-kinds/${kindId}`, { body: update });
    return forward(
      r,
      (j) => parse(EventTicketKind, j),
      (e) => {
        if (e instanceof ValidationError) {
          const refused = asRoleForbidden(e) ?? asHostingOff(e) ?? asShowCancelled(e);
          if (refused) return refused;
          if (e.incorrectValue === "storeId" && e.code === ErrorCode.DoesntExist) return new StoreNotFound(storeId, e);
          if (e.incorrectValue === "eventId") return new EventNotFound(eventId, e);
          if (e.incorrectValue === "kindId") return new TicketKindNotFound(kindId, e);
          return new InvalidFormField(e.incorrectValue, e.detailedDesc, e);
        }
        return e;
      },
    );
  }

  async deleteTicketKind(
    ctx: ApiContext,
    storeId: string,
    eventId: string,
    kindId: string,
  ): Promise<
    Result<
      void,
      | ApiNotAuthorized
      | StoreNotFound
      | StoreRoleForbidden
      | HostingOff
      | EventNotFound
      | ShowCancelled
      | TicketKindNotFound
    >
  > {
    if (!eventId) return fail(new EventNotFound(eventId));
    if (!kindId) return fail(new TicketKindNotFound(kindId));
    const r = await this.t.delete(ctx, `/stores/${storeId}/events/${eventId}/ticket-kinds/${kindId}`);
    return forward(
      r,
      () => undefined,
      (e) => {
        if (e instanceof ValidationError) {
          const refused = asRoleForbidden(e) ?? asHostingOff(e) ?? asShowCancelled(e);
          if (refused) return refused;
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          if (e.incorrectValue === "eventId") return new EventNotFound(eventId, e);
          if (e.incorrectValue === "kindId") return new TicketKindNotFound(kindId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /**
   * Cancels the show for good: it leaves the calendar, the tables being paid for go
   * back, the booths at it close and every payment goes back, HobbyScan's fee included.
   * A refund that does not go through stays owed, on `event.refundsOwed`; calling it
   * again sends what is still owed.
   */
  async cancel(
    ctx: ApiContext,
    storeId: string,
    eventId: string,
  ): Promise<Result<HostedEventRefunds, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | EventNotFound>> {
    if (!eventId) return fail(new EventNotFound(eventId));
    const r = await this.t.post(ctx, `/stores/${storeId}/events/${eventId}/cancel`);
    return this.refunds(r, storeId, eventId);
  }

  /** Sends again the refunds a show still owes, cancelled or not. */
  async retryRefunds(
    ctx: ApiContext,
    storeId: string,
    eventId: string,
  ): Promise<Result<HostedEventRefunds, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | EventNotFound>> {
    if (!eventId) return fail(new EventNotFound(eventId));
    const r = await this.t.post(ctx, `/stores/${storeId}/events/${eventId}/refunds/retry`);
    return this.refunds(r, storeId, eventId);
  }

  private refunds(
    r: Result<unknown, TransportError>,
    storeId: string,
    eventId: string,
  ): Result<HostedEventRefunds, ApiNotAuthorized | StoreNotFound | StoreRoleForbidden | EventNotFound> {
    return forward(
      r,
      (j) => parse(HostedEventRefunds, j),
      (e) => {
        if (e instanceof ValidationError) {
          const forbidden = asRoleForbidden(e);
          if (forbidden) return forbidden;
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          if (e.incorrectValue === "eventId") return new EventNotFound(eventId, e);
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }

  /**
   * Takes a sold table back and refunds the shop that paid for it, HobbyScan's fee
   * included; the table goes back on sale. Answers with the table's kind. When Stripe
   * refuses the refund nothing changes, and `TableRefundRefused` says why.
   */
  async refundTable(
    ctx: ApiContext,
    storeId: string,
    eventId: string,
    tableId: string,
  ): Promise<
    Result<
      EventTableKind,
      | ApiNotAuthorized
      | StoreNotFound
      | StoreRoleForbidden
      | EventNotFound
      | ShowCancelled
      | TableNotFound
      | TableRefundRefused
    >
  > {
    if (!eventId) return fail(new EventNotFound(eventId));
    if (!tableId) return fail(new TableNotFound(tableId));
    const r = await this.t.post(ctx, `/stores/${storeId}/events/${eventId}/tables/${tableId}/refund`);
    return forward(
      r,
      (j) => parse(EventTableKind, j),
      (e) => {
        if (e instanceof ValidationError) {
          const refused = asRoleForbidden(e) ?? asShowCancelled(e);
          if (refused) return refused;
          if (e.incorrectValue === "storeId") return new StoreNotFound(storeId, e);
          if (e.incorrectValue === "eventId") return new EventNotFound(eventId, e);
          if (e.incorrectValue === "tableId") {
            return e.code === ErrorCode.DoesntExist
              ? new TableNotFound(tableId, e)
              : new TableRefundRefused(e.detailedDesc, e);
          }
          throw new UnexpectedApiError(e);
        }
        return e;
      },
    );
  }
}
